import React from 'react';
import { Box, Typography, Container, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';

const sections = [
  {
    title: '1. Purpose',
    paragraphs: [
      'FILM-HOOK MEDIAAPPS PRIVATE LIMITED ("Filmhook Apps") is committed to providing a safe, secure, and professional platform for individuals working in or interested in the entertainment, media, creative, business, and related industries.',
      'Filmhook Apps is designed exclusively for adults aged 18 years and above. The platform is not intended for children or minors, and we do not knowingly provide our services directly to individuals under the age of 18.',
      'This Child Safety Policy explains our commitment to protecting children, preventing exploitation, and complying with applicable child protection laws and platform requirements.'
    ]
  },
  {
    title: '2. Age Requirement',
    paragraphs: [
      'Filmhook Apps is intended only for users who are at least 18 years of age or the age of majority required under applicable law in their jurisdiction.',
      'By creating an account or using Filmhook Apps, users represent and warrant that:'
    ],
    bullets: [
      'They are 18 years of age or older.',
      'They have the legal capacity to enter into binding agreements.',
      'The information provided during registration is accurate and truthful.',
      'They are eligible to use our services under applicable laws.'
    ],
    footerParagraph: 'Users who do not satisfy these requirements must not create or use a Filmhook Apps account.'
  },
  {
    title: '3. No Services for Children',
    paragraphs: [
      'Filmhook Apps does not intentionally offer its services to children or design its platform for use by minors.',
      'Accordingly:'
    ],
    bullets: [
      'Individuals under the age of 18 are not permitted to register an account.',
      'Children may not independently create, access, or operate a Filmhook Apps account.',
      'We do not knowingly target children through our services, advertising, or platform features.',
      'Our platform is intended for professional networking, recruitment, business collaboration, and marketplace activities suitable for adults.'
    ]
  },
  {
    title: '4. Parent or Legal Guardian Responsibility',
    paragraphs: [
      'If, under applicable law, a parent or legal guardian permits a minor to appear in content, participate in a project, or otherwise be represented through Filmhook Apps, the parent or legal guardian remains solely responsible for:'
    ],
    bullets: [
      'Supervising the minor\'s participation.',
      'Managing all communications on behalf of the minor.',
      'Providing any legally required consent.',
      'Protecting the minor\'s privacy and personal information.',
      'Ensuring compliance with applicable child protection laws.'
    ],
    footerParagraph: 'Filmhook Apps does not permit minors to independently manage or operate user accounts.'
  },
  {
    title: '5. Detection of Underage Accounts',
    paragraphs: [
      'If Filmhook Apps becomes aware that an account has been created or is being operated by an individual under the age of 18, we may take appropriate action, including:'
    ],
    bullets: [
      'Requesting age verification.',
      'Restricting account access.',
      'Suspending the account.',
      'Permanently removing the account.',
      'Removing associated content where appropriate.'
    ],
    footerParagraph: 'These actions are intended to protect children and maintain compliance with applicable laws and platform policies.'
  },
  {
    title: '6. Child Safety Commitment',
    paragraphs: [
      'Filmhook Apps maintains a zero-tolerance policy toward any activity involving:'
    ],
    bullets: [
      'Child Sexual Abuse Material (CSAM).',
      'Child sexual exploitation.',
      'Child grooming.',
      'Child trafficking.',
      'Sexual solicitation involving minors.',
      'Content that endangers children.',
      'Abuse or harassment of children.',
      'Any illegal activity involving minors.'
    ],
    footerParagraph: 'Any such content or behavior is strictly prohibited and may result in immediate account suspension or termination. Where required by law, Filmhook Apps may preserve relevant information and cooperate with law enforcement authorities.'
  },
  {
    title: '7. Reporting Child Safety Concerns',
    paragraphs: [
      'Users are encouraged to report any content, communication, profile, or activity that appears to involve:'
    ],
    bullets: [
      'Child exploitation.',
      'Child abuse.',
      'Grooming.',
      'Child trafficking.',
      'Illegal child-related activities.',
      'Any content that places a child at risk.'
    ],
    footerParagraph: 'Reports may be submitted through the in-app reporting tools or by contacting Filmhook Apps through our official support channels.\nAll reports are reviewed as a priority, and appropriate action may be taken in accordance with our Terms of Service, Community Guidelines, and applicable laws.'
  },
  {
    title: '8. Protection of Children\'s Privacy',
    paragraphs: [
      'Because Filmhook Apps is not intended for users under 18 years of age, we do not knowingly collect personal information directly from children.',
      'If we become aware that personal information has been collected from a child without appropriate legal authorization, we may:'
    ],
    bullets: [
      'Remove the account.',
      'Delete the associated personal information, where permitted by law.',
      'Restrict further access to our services.',
      'Take any additional action required under applicable legal obligations.'
    ]
  },
  {
    title: '9. Compliance with Child Protection Laws',
    paragraphs: [
      'Filmhook Apps is committed to complying with applicable child protection laws and regulations, including, where applicable:'
    ],
    bullets: [
      'Protection of Children from Sexual Offences Act, 2012 (India).',
      'Information Technology Act, 2000.',
      'Information Technology (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021.',
      'Digital Personal Data Protection Act, 2023.',
      'Google Play Child Safety Standards.',
      'Other applicable child protection laws and regulatory requirements.'
    ]
  }
];

const ChildSafety = () => {
  return (
    <Box sx={{ py: { xs: 4, sm: 8, md: 12 }, minHeight: '80vh' }}>
      <Container maxWidth="md">
        <Box
          sx={{
            background: 'var(--bg-card)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid var(--border-color)',
            borderRadius: '24px',
            p: { xs: 2.5, sm: 4, md: 7 },
            boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
          }}
        >
          <Typography variant="h3" fontWeight={800} gutterBottom sx={{ color: 'var(--text-heading)', textAlign: 'center', mb: 1, fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3rem' } }}>
            Child Safety Policy
          </Typography>
          <Typography variant="h6" sx={{ color: '#5dade2', textAlign: 'center', mb: 3, fontWeight: 500, letterSpacing: '0.5px', fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.25rem' } }}>
            Age Eligibility & Protection of Minors
          </Typography>
          <Box sx={{ width: 80, height: 3, background: 'linear-gradient(90deg, #07a4f5, #5dade2)', mx: 'auto', mb: 6, borderRadius: '2px' }} />

          {sections.map((section, idx) => (
            <Box key={idx} sx={{ mb: 6 }}>
              <Typography variant="h5" fontWeight={700} gutterBottom sx={{ color: 'var(--text-heading)', mb: 2, fontSize: { xs: '1.2rem', sm: '1.35rem', md: '1.5rem' } }}>
                {section.title}
              </Typography>
          
              {section.paragraphs.map((p, pIdx) => (
                <Typography key={pIdx} variant="body1" sx={{ color: 'var(--text-secondary)', lineHeight: 1.8, mb: 2, textAlign: 'justify', fontSize: { xs: '0.88rem', md: '1rem' } }}>
                  {p}
                </Typography>
              ))}

              {section.bullets && (
                <List sx={{ pl: 2, mb: 2 }}>
                  {section.bullets.map((bullet, bIdx) => (
                    <ListItem key={bIdx} alignItems="flex-start" disableGutters sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: '32px', mt: '8px' }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#5dade2' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body1" sx={{ color: 'var(--text-secondary)', lineHeight: 1.7, textAlign: 'justify', fontSize: { xs: '0.88rem', md: '1rem' } }}>
                            {bullet}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}

              {section.footerParagraph && section.footerParagraph.split('\n').map((fp, fpIdx) => (
                <Typography key={fpIdx} variant="body1" sx={{ color: 'var(--text-secondary)', lineHeight: 1.8, mt: 2, textAlign: 'justify', fontSize: { xs: '0.88rem', md: '1rem' } }}>
                  {fp}
                </Typography>
              ))}

              {idx < sections.length - 1 && (
                <Box sx={{ height: '1px', background: 'var(--border-subtle)', mt: 5 }} />
              )}
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default ChildSafety;
